import React, { useState, useEffect } from "react";
import { apiClient } from "../../../services/api/apiClient";
import toast from "react-hot-toast";
import { appInsights } from "../../../services/analytics/appInsights";
import { useMsal } from "@azure/msal-react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import { CheckIcon } from '@heroicons/react/20/solid';

export default function GeneratePDFModal({ onClose, submittalId }) {
  const [selectedCutsheets, setSelectedCutsheets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingSeparately, setIsGeneratingSeparately] = useState(false);
  const [specNames, setSpecNames] = useState([]);
  const [loadingSpecs, setLoadingSpecs] = useState(true);
  const [separateProgress, setSeparateProgress] = useState({
    current: 0,
    total: 0,
  });
  const [selectedSpecs, setSelectedSpecs] = useState([]);

  // Define available cutsheet types
  const cutsheetTypes = ['Pipes & Fittings', 'Valves', 'Hangers'];

  const { accounts } = useMsal();
  const userName = accounts && accounts[0] ? accounts[0].name : "Unknown";

  // Fetch spec names when modal opens
  useEffect(() => {
    const fetchSpecNames = async () => {
      try {
        setLoadingSpecs(true);
        const response = await apiClient.get(`/pdf/${submittalId}/specs`);
        const specs = response.data?.specNames || [];
        
        // Add hanger options to the specifications list
        const allOptions = [
          ...specs,
          'HVAC Hangers',
          'Plumbing Hangers'
        ];
        
        setSpecNames(allOptions);
        // Default to all specifications selected (including hangers)
        setSelectedSpecs(allOptions);
      } catch (error) {
        console.error("Error fetching spec names:", error);
        toast.error("Failed to load specifications");
      } finally {
        setLoadingSpecs(false);
      }
    };

    if (submittalId) {
      fetchSpecNames();
    }
  }, [submittalId]);

  // Helper function to convert cutsheet selection to query parameters
  const getCutsheetQueryParams = () => {
    const cutsheetMap = {
      'Pipes & Fittings': 'pipe',
      'Valves': 'valve',
      'Hangers': 'hanger'
    };
    
    return selectedCutsheets
      .map(cutsheet => `${cutsheetMap[cutsheet]}=true`)
      .join('&');
  };

  const generateCombinedPDF = async () => {
    setIsLoading(true);
    const toastId = toast.loading("Generating combined submittal...");

    // Build query parameters for selected cutsheets
    const checkboxParams = getCutsheetQueryParams();

    // Build query parameters for selected specifications
    // For multiple specs, send as a JSON array to avoid URL encoding issues
    const specParams = selectedSpecs.length > 1 
      ? `specs=${encodeURIComponent(JSON.stringify(selectedSpecs))}`
      : selectedSpecs.map(spec => `specs=${encodeURIComponent(spec)}`).join("&");

    let url = `/pdf/${submittalId}`;
    const allParams = [];

    if (checkboxParams) {
      allParams.push(checkboxParams);
    }
    if (specParams) {
      allParams.push(specParams);
    }

    if (allParams.length > 0) {
      url += `?${allParams.join("&")}`;
    }

    try {
      const response = await apiClient.get(url);

      console.log("Here is the pdf response data", response);
      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);

      const data = response.data;
      console.log("Response data structure:", data);
      console.log("Data success:", data.success);
      console.log("Data data:", data.data);

        if (data.success) {
          // Extract the actual PDF data from the nested structure
          const pdfData = data.data || data;
          
          // Track successful combined PDF generation
          appInsights.trackEvent({
            name: 'PDFGenerated',
            properties: {
              type: 'Combined',
              user: userName,
              submittalId: submittalId,
              includePipes: selectedCutsheets.includes('Pipes & Fittings'),
              includeValves: selectedCutsheets.includes('Valves'),
              includeHangers: selectedCutsheets.includes('Hangers')
            }
          });
          
        toast.success("Combined submittal generated successfully!", {
          id: toastId,
        });
        console.log("Combined submittal generated successfully");
        console.log("Download URL:", pdfData.downloadUrl);

        // Optionally trigger download automatically
        if (pdfData.downloadUrl) {
          // Create a temporary link to trigger download
          const link = document.createElement("a");
          link.href = pdfData.downloadUrl;
          link.download = pdfData.fileName || "combined_pdf.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        throw new Error(data.error || "Failed to generate combined PDF");
      }
    } catch (error) {
      console.error("Error generating combined PDF:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error("Failed to generate combined PDF", { id: toastId });
    } finally {
      setIsLoading(false);
    }
  };

  const generateSeparatePDFs = async () => {
    console.log('generateSeparatePDFs called with:', { selectedSpecs, selectedCutsheets, specNames });
    
    // Check if any relevant content types are selected
    const hasContentSelected = selectedCutsheets.length > 0;
    
    if (selectedSpecs.length === 0) {
      console.log('Early return: no specs selected');
      toast.error("No specifications or hangers found to generate");
      return;
    }
    


    console.log('Starting separate PDF generation...');
    setIsGeneratingSeparately(true);

    // Calculate total PDFs to generate
    let totalPdfs = 0;
    
    // Count only regular specifications (exclude hanger options)
    const regularSpecs = selectedSpecs.filter(spec => !spec.includes('Hangers'));
    totalPdfs += regularSpecs.length;
  
    
    // Count selected hanger options
    const selectedHangers = selectedSpecs.filter(spec => spec.includes('Hangers'));
    totalPdfs += selectedHangers.length;

    setSeparateProgress({ current: 0, total: totalPdfs });

    const toastId = toast.loading("Generating separate PDFs...");
    let successCount = 0;
    let errorCount = 0;
    let currentCount = 0;

    // Generate query params excluding hangers for spec-based PDFs
    const specQueryParams = selectedCutsheets
      .filter(cutsheet => cutsheet !== 'Hangers') // Exclude hangers from spec PDFs
      .map(cutsheet => {
        const cutsheetMap = {
          'Pipes & Fittings': 'pipe',
          'Valves': 'valve'
        };
        return `${cutsheetMap[cutsheet]}=true`;
      })
      .join("&");

    try {
      // Generate spec-based PDFs (pipes & valves only)
      
        const regularSpecs = selectedSpecs.filter(spec => !spec.includes('Hangers'));
        
        for (let i = 0; i < regularSpecs.length; i++) {
          const specName = regularSpecs[i];
          currentCount++;
          setSeparateProgress({ current: currentCount, total: totalPdfs });

          toast.loading(
            `Generating PDF ${currentCount}/${totalPdfs}: "${specName}"`,
            { id: toastId }
          );

          try {
            const url = `/pdf/${submittalId}?${specQueryParams}&specs=${encodeURIComponent(JSON.stringify([specName]))}`;
            console.log(`Generating PDF for spec "${specName}" with URL:`, url);
            const response = await apiClient.get(url);
            console.log(`Response for spec "${specName}":`, response);
            const data = response.data;
            console.log(`Data for spec "${specName}":`, data);

            if (data.success) {
              // Extract the actual PDF data from the nested structure
              const pdfData = data.data || data;
              
              successCount++;
              console.log(
                `PDF for spec "${specName}" generated successfully`
              );
              console.log(`Download URL: ${pdfData.downloadUrl}`);

              // Track successful separate PDF generation for each spec
              appInsights.trackEvent({
                name: 'PDFGenerated',
                properties: {
                  type: 'Separate',
                  user: userName,
                  submittalId: submittalId,
                  specName: specName,
                  includePipes: selectedCutsheets.includes('Pipes & Fittings'),
                  includeValves: selectedCutsheets.includes('Valves'),
                  includeHangers: selectedCutsheets.includes('Hangers')
                }
              });

              // Optionally trigger download automatically
              if (pdfData.downloadUrl) {
                const link = document.createElement("a");
                link.href = pdfData.downloadUrl;
                link.download = pdfData.fileName || `${specName}_pdf.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            } else {
              throw new Error(
                data.error || `Failed to generate PDF for spec "${specName}"`
              );
            }
          
          } catch (error) {
            console.error(
              `Error generating PDF for spec "${specName}":`,
              error
            );
            errorCount++;
          }

          if (currentCount < totalPdfs) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }
      

      // Generate hanger PDFs for selected hanger options
      const selectedHangers = selectedSpecs.filter(spec => spec.includes('Hangers'));
     
        if (selectedHangers.length > 0 ) {
        for (const hangerType of selectedHangers) {
          const trade = hangerType === 'HVAC Hangers' ? 'HVAC' : 'Plumbing';
          currentCount++;
          setSeparateProgress({ current: currentCount, total: totalPdfs });

          toast.loading(
            `Generating PDF ${currentCount}/${totalPdfs}: "${hangerType}"`,
            { id: toastId }
          );

          try {
            // Calculate onlyTable flag (same logic as backend)

            console.log("selectedCutsheets:", selectedCutsheets);
            
            const onlyTable = selectedCutsheets.includes('Hangers') ? false : true;
            console.log("onlyTable:", onlyTable);

            const url = `/pdf/${submittalId}/hangers/${trade}${
              onlyTable ? "?onlyTable=true" : ""
            }`;

            const response = await apiClient.get(url);
            console.log(`Response for hanger ${hangerType}:`, response);
            const data = response.data;
            console.log(`Data for hanger ${hangerType}:`, data);

            if (data.success) {
              // Extract the actual PDF data from the nested structure
              const pdfData = data.data || data;
              
              successCount++;
              console.log(`PDF for ${hangerType} generated successfully`);
              console.log(`Download URL: ${pdfData.downloadUrl}`);

              // Track successful separate PDF generation for hangers
              appInsights.trackEvent({
                name: 'PDFGenerated',
                properties: {
                  type: 'Separate',
                  user: userName,
                  submittalId: submittalId,
                  trade: trade,
                  hangerType: 'Hangers',
                  includePipes: selectedCutsheets.includes('Pipes & Fittings'),
                  includeValves: selectedCutsheets.includes('Valves'),
                  includeHangers: selectedCutsheets.includes('Hangers')
                }
              });

              // Optionally trigger download automatically
              if (pdfData.downloadUrl) {
                const link = document.createElement("a");
                link.href = pdfData.downloadUrl;
                link.download = pdfData.fileName || `${hangerType}_pdf.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            } else {
              throw new Error(
                data.error || `Failed to generate PDF for ${hangerType}`
              );
            }
          } catch (error) {
            console.error(`Error generating PDF for ${hangerType}:`, error);
            errorCount++;
          }

          if (currentCount < totalPdfs) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }
      }

      if (errorCount === 0) {
        toast.success(`All ${successCount} PDFs generated successfully!`, {
          id: toastId,
        });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else if (successCount > 0) {
        toast.success(
          `${successCount} PDFs generated successfully, ${errorCount} failed`,
          { id: toastId }
        );
        // Close modal even with partial success
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        toast.error(`Failed to generate all PDFs`, { id: toastId });
      }
    } catch (error) {
      console.error("Error in batch PDF generation:", error);
      toast.error("Failed to generate separate PDFs", { id: toastId });
    } finally {
      setIsGeneratingSeparately(false);
      setSeparateProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-1/3 max-w-lg max-h-[32rem] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
          Generate PDF Submittal Package
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 pt-6 flex flex-col">
          <div className="w-full">
            <div className="space-y-5 flex flex-col ml-4">
              {/* Specifications Selection Dropdown */}
              <div className="flex flex-col space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select Specifications to Include
                </label>
                <div className="relative">
                  <Listbox 
                    value={selectedSpecs} 
                    onChange={(newSelection) => {
                      // Handle special cases for Select All and Deselect All
                      if (newSelection.includes('__select_all__')) {
                        setSelectedSpecs([...specNames]);
                        return;
                      }
                      if (newSelection.includes('__deselect_all__')) {
                        setSelectedSpecs([]);
                        return;
                      }
                      // Handle regular selections (filter out special values)
                      const regularSelections = newSelection.filter(item => 
                        !item.startsWith('__')
                      );
                      setSelectedSpecs(regularSelections);
                    }}
                    multiple
                    disabled={loadingSpecs || specNames.length === 0}
                  >
                    <ListboxButton className="grid w-full cursor-default grid-cols-1 rounded-md bg-white py-2 pr-2 pl-3 text-left text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600 sm:text-sm border border-gray-300 min-h-12 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed">
                      <span className="col-start-1 row-start-1 flex w-full gap-2 pr-6">
                        {loadingSpecs ? (
                          <span className="text-gray-500">Loading specifications...</span>
                        ) : specNames.length === 0 ? (
                          <span className="text-gray-500">No specifications available</span>
                        ) : selectedSpecs.length === 0 ? (
                          <span className="text-gray-500">No Specifications Selected</span>
                        ) : selectedSpecs.length === specNames.length ? (
                          <span className="text-gray-500">All Specifications Selected</span>
                        ) : selectedSpecs.length === 1 ? (
                          <span className="truncate">{selectedSpecs[0]}</span>
                        ) : (
                          <span className="truncate">{selectedSpecs.length} specifications selected</span>
                        )}
                      </span>
                      <ChevronUpDownIcon
                        aria-hidden="true"
                        className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                      />
                    </ListboxButton>

                    <ListboxOptions
                      transition
                      className="absolute z-10 mt-1 max-h-60 w-max min-w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-1 outline-black/5 data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm border border-gray-200"
                    >
                      {/* Select All / Deselect All Options */}
                      <ListboxOption
                        value="__select_all__"
                        className="group relative cursor-default py-2 pr-9 pl-3 text-blue-600 select-none data-focus:bg-blue-600 data-focus:text-white data-focus:outline-hidden border-b border-gray-100"
                      >
                        <div className="flex items-center">
                          <div className="w-5 flex-shrink-0">
                          </div>
                          <span className="whitespace-nowrap font-semibold">Select All</span>
                        </div>
                      </ListboxOption>
                      
                      <ListboxOption
                        value="__deselect_all__"
                        className="group relative cursor-default py-2 pr-9 pl-3 text-gray-600 select-none data-focus:bg-blue-600 data-focus:text-white data-focus:outline-hidden border-b border-gray-100"
                      >
                        <div className="flex items-center">
                          <div className="w-5 flex-shrink-0">
                          </div>
                          <span className="whitespace-nowrap font-semibold">Clear Selections</span>
                        </div>
                      </ListboxOption>
                      {specNames.map((specName) => (
                        <ListboxOption
                          key={specName}
                          value={specName}
                          className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-blue-600 data-focus:text-white data-focus:outline-hidden"
                        >
                          <div className="flex items-center">
                            <div className="w-5 flex-shrink-0">
                              {selectedSpecs.includes(specName) && (
                                <CheckIcon aria-hidden="true" className="size-5 text-blue-600 group-data-focus:text-white" />
                              )}
                            </div>
                            <span className="whitespace-nowrap font-normal group-data-selected:font-semibold">{specName}</span>
                          </div>
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Listbox>
                </div>
                <div className="text-xs text-gray-500">
                  {selectedSpecs.length === 0 ? "No Specifications Selected" : 
                   selectedSpecs.length === specNames.length ? "All Specifications Selected" : 
                   `${selectedSpecs.length} of ${specNames.length} specifications selected`}
                </div>
              </div>

              {/* Cutsheet Types Selection Dropdown */}
              <div className="flex flex-col space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select Cutsheets to Include
                </label>
                <div className="relative">
                  <Listbox 
                    value={selectedCutsheets} 
                    onChange={setSelectedCutsheets} 
                    multiple
                  >
                    <ListboxButton className="grid w-full cursor-default grid-cols-1 rounded-md bg-white py-2 pr-2 pl-3 text-left text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600 sm:text-sm border border-gray-300 min-h-12 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed">
                      <span className="col-start-1 row-start-1 flex w-full gap-2 pr-6">
                        {selectedCutsheets.length === 0 ? (
                          <span className="text-gray-500">No Cutsheets Selected</span>
                        ) : selectedCutsheets.length === cutsheetTypes.length ? (
                          <span className="text-gray-500">All Cutsheets Selected</span>
                        ) : selectedCutsheets.length === 1 ? (
                          <span className="truncate">{selectedCutsheets[0]}</span>
                        ) : (
                          <span className="truncate">{selectedCutsheets.length} cutsheet types selected</span>
                        )}
                      </span>
                      <ChevronUpDownIcon
                        aria-hidden="true"
                        className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                      />
                    </ListboxButton>

                    <ListboxOptions
                      transition
                      className="absolute z-10 mt-1 max-h-60 w-max min-w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-1 outline-black/5 data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm border border-gray-200"
                    >
                      {cutsheetTypes.map((cutsheetType) => (
                        <ListboxOption
                          key={cutsheetType}
                          value={cutsheetType}
                          className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-blue-600 data-focus:text-white data-focus:outline-hidden"
                        >
                          <div className="flex items-center">
                            <div className="w-5 flex-shrink-0">
                              {selectedCutsheets.includes(cutsheetType) && (
                                <CheckIcon aria-hidden="true" className="size-5 text-blue-600 group-data-focus:text-white" />
                              )}
                            </div>
                            <span className="whitespace-nowrap font-normal group-data-selected:font-semibold">{cutsheetType}</span>
                          </div>
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Listbox>
                </div>
                <div className="text-xs text-gray-500">
                  {selectedCutsheets.length === 0 ? "No Cutsheet Types Selected" : 
                   selectedCutsheets.length === cutsheetTypes.length ? "All Cutsheet Types Selected" : 
                   `${selectedCutsheets.length} of ${cutsheetTypes.length} cutsheet types selected`}
                </div>
              </div>
            </div>
          </div>

          {/* Progress indicator for separate generation */}
          {isGeneratingSeparately && (
            <div className="mt-4 w-full">
              <div className="text-xs text-gray-600 mb-2 text-center">
                Generating separate PDFs: {separateProgress.current} of{" "}
                {separateProgress.total}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      (separateProgress.current / separateProgress.total) * 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-10 mr-4">
            <button
              onClick={onClose}
              disabled={isLoading || isGeneratingSeparately}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                isLoading || isGeneratingSeparately
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Close
            </button>
            <button
              disabled={isLoading || isGeneratingSeparately}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                isLoading || isGeneratingSeparately
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              onClick={generateCombinedPDF}
            >
              {isLoading ? "Generating..." : "Combined Submittal"}
            </button>
            <button
              disabled={
                isLoading ||
                isGeneratingSeparately ||
                loadingSpecs ||
                specNames.length === 0
              }
              className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                isLoading ||
                isGeneratingSeparately ||
                loadingSpecs ||
                specNames.length === 0
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
              onClick={generateSeparatePDFs}
            >
              {isGeneratingSeparately
                ? `Generating... (${separateProgress.current}/${separateProgress.total})`
                : loadingSpecs
                ? "Loading..."
                : `By Specification${
                    selectedSpecs.length > 0 ? ` (${selectedSpecs.length})` : ""
                  }`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
